import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  Cpu, 
  Code, 
  Hammer, 
  Save, 
  Clock, 
  Upload, 
  Plus, 
  Trash2
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const DEFAULT_TECH_DATA = {
  'smart-classrooms': {
    title: 'Smart Classrooms',
    subtitle: 'Interactive Learning Environments',
    description: 'Classrooms equipped with cutting-edge technology to create engaging, personalized learning experiences that inspire curiosity and collaboration.',
    color: '#3b82f6',
    colorLight: '#dbeafe',
    icon: 'fa-chalkboard',
    images: ['/assets/news/news3.jpg', '/assets/news/news5.jpg', '/assets/news/news6.jpg'],
    features: [
      {
        icon: 'fa-tv',
        title: 'Interactive Displays',
        detail: 'Large touchscreen boards for dynamic presentations and real-time collaboration'
      },
      {
        icon: 'fa-wifi',
        title: 'High-Speed Connectivity',
        detail: 'Reliable wireless network supporting seamless digital learning experiences'
      },
      {
        icon: 'fa-laptop',
        title: 'Device Integration',
        detail: 'Seamless connection for student devices, enabling BYOD learning models'
      },
      {
        icon: 'fa-cloud',
        title: 'Cloud-Based Resources',
        detail: 'Access to digital libraries, educational apps, and collaborative platforms'
      }
    ],
    highlights: [
      'Real-time student engagement tracking',
      'Multimedia content delivery capabilities',
      'Flexible furniture for collaborative learning',
      'Teacher training and technical support included',
      'Accessibility features for all learners'
    ],
    content: 'Smart Classrooms represent the future of education. These spaces integrate technology seamlessly into the learning environment, allowing teachers to create dynamic, interactive lessons that cater to diverse learning styles. Students benefit from multimedia content, real-time feedback, and opportunities for digital collaboration. The technology supports both traditional instruction and innovative pedagogies like flipped classrooms and project-based learning. Teachers receive comprehensive training to maximize the potential of these tools, ensuring technology enhances rather than distracts from learning objectives.',
    benefits: [
      'Enhanced student engagement and participation',
      'Personalized learning pathways',
      'Real-time assessment and feedback',
      'Increased collaboration opportunities',
      'Preparation for digital-age careers'
    ]
  },
  'it-lab': {
    title: 'IT Lab',
    subtitle: 'Computing & Digital Literacy Hub',
    description: 'A dedicated space where students develop computational thinking, coding skills, and digital literacy essential for the modern world.',
    color: '#10b981',
    colorLight: '#d1fae5',
    icon: 'fa-code',
    images: ['/assets/news/news5.jpg', '/assets/news/news6.jpg', '/assets/news/news8.jpg'],
    features: [
      {
        icon: 'fa-desktop',
        title: 'Modern Workstations',
        detail: 'High-performance computers with latest software and development tools'
      },
      {
        icon: 'fa-graduation-cap',
        title: 'Coding Platforms',
        detail: 'Access to industry-standard programming languages and learning environments'
      },
      {
        icon: 'fa-network-wired',
        title: 'Network Infrastructure',
        detail: 'Secure, fast network for collaborative projects and online learning'
      },
      {
        icon: 'fa-book-open',
        title: 'Learning Resources',
        detail: 'Comprehensive tutorials, documentation, and project templates'
      }
    ],
    highlights: [
      'Capacity: 30 students per session',
      'Multiple programming languages supported',
      'Project-based learning approach',
      'Industry-standard tools and software',
      'Regular workshops and coding competitions'
    ],
    content: 'The IT Lab is where digital natives become digital creators. Students learn to code, understand algorithms, and develop problem-solving skills through hands-on projects. The lab supports various programming languages and frameworks, from beginner-friendly platforms like Scratch to professional tools like Python and JavaScript. Students work on real-world projects, participate in coding competitions, and build portfolios that prepare them for tech careers. The collaborative environment encourages peer learning and mentorship, fostering a community of young innovators.',
    benefits: [
      'Computational thinking development',
      'Career readiness in tech fields',
      'Problem-solving and logic skills',
      'Digital citizenship awareness',
      'Entrepreneurial mindset cultivation'
    ]
  },
  'maker-space': {
    title: 'Maker Space',
    subtitle: 'Innovation & Design Studio',
    description: 'A creative hub where students transform ideas into reality through hands-on making, prototyping, and collaborative design thinking.',
    color: '#f59e0b',
    colorLight: '#fef3c7',
    icon: 'fa-hammer',
    images: ['/assets/news/news6.jpg', '/assets/news/news3.jpg', '/assets/news/news5.jpg'],
    features: [
      {
        icon: 'fa-cube',
        title: '3D Printing & Design',
        detail: 'CAD software and 3D printers for rapid prototyping and design exploration'
      },
      {
        icon: 'fa-microchip',
        title: 'Electronics & Robotics',
        detail: 'Microcontrollers, sensors, and robotics kits for STEM projects'
      },
      {
        icon: 'fa-tools',
        title: 'Hand & Power Tools',
        detail: 'Safe, supervised access to tools for woodworking, metalworking, and crafting'
      },
      {
        icon: 'fa-lightbulb',
        title: 'Design Thinking',
        detail: 'Structured methodology for innovation and creative problem-solving'
      }
    ],
    highlights: [
      'Open-ended project opportunities',
      'Interdisciplinary collaboration',
      'Safety training and supervision',
      'Access to diverse materials and tools',
      'Showcase opportunities for student work'
    ],
    content: 'The Maker Space embodies the spirit of innovation and creativity. It is a place where students from different disciplines come together to design, build, and iterate. Whether creating a robot, designing a product, or building an art installation, students learn through doing. The space encourages experimentation, celebrates failure as part of learning, and fosters a growth mindset. With access to 3D printers, electronics, traditional tools, and materials, students have the resources to bring any idea to life. Regular workshops, maker fairs, and project showcases celebrate student creativity and innovation.',
    benefits: [
      'Creative expression and innovation',
      'Hands-on STEM learning',
      'Entrepreneurial skills development',
      'Interdisciplinary thinking',
      'Confidence in making and building'
    ]
  }
};

const TECH_ITEMS = [
  { id: 'smart-classrooms', name: 'Smart Classrooms', icon: Cpu },
  { id: 'it-lab', name: 'IT Lab', icon: Code },
  { id: 'maker-space', name: 'Maker Space', icon: Hammer }
];

export default function FacilitiesTechDetailForm({ apiBase = 'https://permataharapansecondary.sch.id/api' }) {
  const toast = useToast();
  const [selectedTechId, setSelectedTechId] = useState('smart-classrooms');
  const [technologiesData, setTechnologiesData] = useState(DEFAULT_TECH_DATA);
  const [originalData, setOriginalData] = useState(DEFAULT_TECH_DATA);
  const [sectionId, setSectionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [pendingFields, setPendingFields] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [imageUploading, setImageUploading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        const items = json.data?.items || json.data || [];
        if (Array.isArray(items)) {
          const map = {};
          items.forEach(m => {
            if (m.id && m.file_path) {
              map[m.id] = m.file_path;
              map[String(m.id)] = m.file_path;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (e) {
      console.warn('[FacilitiesTechDetailForm] Media fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=facilities`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'facilities-tech-details' || s.section_type === 'technology-details');
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let rawContent = foundSection.content_published || foundSection.content;
        if (typeof rawContent === 'string') {
          try {
            rawContent = JSON.parse(rawContent);
          } catch (e) {
            rawContent = {};
          }
        }

        const dataEn = rawContent?.en || rawContent || {};
        const merged = { ...DEFAULT_TECH_DATA };
        Object.keys(DEFAULT_TECH_DATA).forEach(key => {
          if (dataEn[key]) {
            merged[key] = {
              ...DEFAULT_TECH_DATA[key],
              ...dataEn[key]
            };
          }
        });

        setTechnologiesData(merged);
        setOriginalData(merged);

        try {
          const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendRes.ok) {
            const pendJson = await pendRes.json();
            if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
              const pendMap = {};
              pendJson.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  pendMap[c.field_id] = true;
                }
              });
              setPendingFields(pendMap);
            }
          }
        } catch (pe) {
          console.warn('[FacilitiesTechDetailForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[FacilitiesTechDetailForm] Load error:', err);
      toast.error('Gagal memuat data Technology & Innovation Details');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const currentTech = technologiesData[selectedTechId] || DEFAULT_TECH_DATA[selectedTechId];

  const handleTechFieldChange = (field, value) => {
    setTechnologiesData(prev => ({
      ...prev,
      [selectedTechId]: {
        ...prev[selectedTechId],
        [field]: value
      }
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    setTechnologiesData(prev => {
      const updatedFeatures = [...(prev[selectedTechId].features || [])];
      updatedFeatures[index] = { ...updatedFeatures[index], [field]: value };
      return {
        ...prev,
        [selectedTechId]: {
          ...prev[selectedTechId],
          features: updatedFeatures
        }
      };
    });
  };

  const handleAddFeature = () => {
    setTechnologiesData(prev => ({
      ...prev,
      [selectedTechId]: {
        ...prev[selectedTechId],
        features: [
          ...(prev[selectedTechId].features || []),
          { icon: 'fa-star', title: 'Fitur Baru', detail: 'Keterangan fitur teknologi...' }
        ]
      }
    }));
  };

  const handleRemoveFeature = (index) => {
    setTechnologiesData(prev => ({
      ...prev,
      [selectedTechId]: {
        ...prev[selectedTechId],
        features: (prev[selectedTechId].features || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleHighlightChange = (index, value) => {
    setTechnologiesData(prev => {
      const updated = [...(prev[selectedTechId].highlights || [])];
      updated[index] = value;
      return {
        ...prev,
        [selectedTechId]: {
          ...prev[selectedTechId],
          highlights: updated
        }
      };
    });
  };

  const handleAddHighlight = () => {
    setTechnologiesData(prev => ({
      ...prev,
      [selectedTechId]: {
        ...prev[selectedTechId],
        highlights: [...(prev[selectedTechId].highlights || []), 'Poin spesifikasi atau keunggulan baru']
      }
    }));
  };

  const handleRemoveHighlight = (index) => {
    setTechnologiesData(prev => ({
      ...prev,
      [selectedTechId]: {
        ...prev[selectedTechId],
        highlights: (prev[selectedTechId].highlights || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleBenefitChange = (index, value) => {
    setTechnologiesData(prev => {
      const updated = [...(prev[selectedTechId].benefits || [])];
      updated[index] = value;
      return {
        ...prev,
        [selectedTechId]: {
          ...prev[selectedTechId],
          benefits: updated
        }
      };
    });
  };

  const handleAddBenefit = () => {
    setTechnologiesData(prev => ({
      ...prev,
      [selectedTechId]: {
        ...prev[selectedTechId],
        benefits: [...(prev[selectedTechId].benefits || []), 'Manfaat teknologi baru bagi siswa']
      }
    }));
  };

  const handleRemoveBenefit = (index) => {
    setTechnologiesData(prev => ({
      ...prev,
      [selectedTechId]: {
        ...prev[selectedTechId],
        benefits: (prev[selectedTechId].benefits || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleImageReplace = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format file tidak didukung. Gunakan JPG, PNG, atau WebP.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran file maksimal 5MB.');
      return;
    }

    try {
      setImageUploading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch(`${apiBase}/homepage/cms/media/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });

      if (res.ok) {
        const json = await res.json();
        const uploadedPath = json.data?.file_path || json.file_path || json.url || json.data?.url;
        if (uploadedPath) {
          setTechnologiesData(prev => {
            const updatedImages = [...(prev[selectedTechId].images || [])];
            updatedImages[index] = uploadedPath;
            return {
              ...prev,
              [selectedTechId]: {
                ...prev[selectedTechId],
                images: updatedImages
              }
            };
          });
          toast.success(`Foto slide ${index + 1} berhasil diperbarui.`);
        }
      } else {
        toast.error('Gagal mengupload gambar.');
      }
    } catch (err) {
      console.error('[FacilitiesTechDetailForm] Upload error:', err);
      toast.error('Terjadi kesalahan saat mengupload gambar.');
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  const submitTechSection = async () => {
    const techKey = selectedTechId;
    setSubmitting(prev => ({ ...prev, [techKey]: true }));

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const oldVal = originalData[techKey] ? JSON.stringify(originalData[techKey]) : '{}';
      const newVal = JSON.stringify(technologiesData[techKey]);

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          section_id: sectionId || 'facilities-tech-details',
          field_id: techKey,
          field_name: `Technology Detail - ${currentTech.title}`,
          old_value: oldVal,
          new_value: newVal,
          locale: 'en'
        })
      });

      if (res.ok) {
        const json = await res.json();
        if (json.success) {
          toast.success(`Perubahan detail ${currentTech.title} berhasil diajukan untuk persetujuan Principal.`);
          setPendingFields(prev => ({ ...prev, [techKey]: true }));
        } else {
          toast.error(json.message || 'Gagal mengajukan perubahan detail.');
        }
      } else {
        toast.error('Gagal menghubungi server untuk pengajuan persetujuan.');
      }
    } catch (e) {
      console.error('[FacilitiesTechDetailForm] Submit error:', e);
      toast.error('Terjadi kesalahan saat menyimpan data.');
    } finally {
      setSubmitting(prev => ({ ...prev, [techKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-card-container">
        <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 12px' }} />
          <p>Memuat konfigurasi detail Technology & Innovation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-form-wrapper">
      {/* Header */}
      <div className="unified-header-card">
        <div className="header-info">
          <h2>Technology & Innovation Detail Pages</h2>
          <p>
            Kelola konten detail interaktif, foto carousel, spesifikasi fitur, poin sorotan, dan manfaat teknologi sekolah.
          </p>
        </div>
        <div className="header-actions">
          {pendingFields[selectedTechId] && (
            <span className="field-pending-tag">
              <Clock size={13} /> Menunggu Persetujuan
            </span>
          )}
          <button
            type="button"
            className="unified-btn-primary"
            onClick={submitTechSection}
            disabled={submitting[selectedTechId]}
          >
            <Save size={16} />
            <span>{submitting[selectedTechId] ? 'Menyimpan...' : `Simpan Detail ${currentTech.title}`}</span>
          </button>
        </div>
      </div>

      {/* Technology Selector Tabs */}
      <div className="unified-tab-bar sub-tabs-bar">
        {TECH_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === selectedTechId;
          const isPending = pendingFields[item.id];

          return (
            <button
              key={item.id}
              type="button"
              className={`tab-pill-btn ${isActive ? 'active' : ''}`}
              onClick={() => setSelectedTechId(item.id)}
            >
              <Icon size={16} className="w-4 h-4 mr-2" />
              <span>{item.name}</span>
              {isPending && (
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#eab308', marginLeft: '6px' }} />
              )}
            </button>
          );
        })}
      </div>

      <div className="unified-section-card">
        <div className="card-header" style={{ borderLeft: `6px solid ${currentTech.color}` }}>
          <div className="title-with-icon">
            <Cpu size={20} style={{ color: currentTech.color }} />
            <h3>{currentTech.title} - Informasi Detail</h3>
          </div>
          {pendingFields[selectedTechId] && (
            <span className="field-pending-tag">Pending Review</span>
          )}
        </div>
        {/* Basic Info */}
        <div className="card-body form-grid-2">
          <div className="form-group">
            <label>Title (Nama Ruang / Teknologi)</label>
            <input
              type="text"
              value={currentTech.title}
              onChange={e => handleTechFieldChange('title', e.target.value)}
              placeholder="Contoh: Smart Classrooms"
            />
          </div>

          <div className="form-group">
            <label>Subtitle (Slogan / Sub-judul)</label>
            <input
              type="text"
              value={currentTech.subtitle}
              onChange={e => handleTechFieldChange('subtitle', e.target.value)}
              placeholder="Contoh: Interactive Learning Environments"
            />
          </div>

          <div className="form-group">
            <label>Icon FontAwesome Class</label>
            <input
              type="text"
              value={currentTech.icon}
              onChange={e => handleTechFieldChange('icon', e.target.value)}
              placeholder="fa-chalkboard, fa-code, fa-hammer"
            />
          </div>

          <div className="form-group">
            <label>Theme Color (Hex)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={currentTech.color}
                onChange={e => handleTechFieldChange('color', e.target.value)}
                style={{ width: '45px', height: '38px', padding: 0, cursor: 'pointer' }}
              />
              <input
                type="text"
                value={currentTech.color}
                onChange={e => handleTechFieldChange('color', e.target.value)}
                placeholder="#3b82f6"
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label>Short Description (Sub-header)</label>
            <textarea
              rows={2}
              value={currentTech.description}
              onChange={e => handleTechFieldChange('description', e.target.value)}
              placeholder="Deskripsi singkat teknologi..."
            />
          </div>

          {/* 3 Slider Images */}
          <div className="form-group full-width">
            <label>Detail Carousel Images (3 Foto Slide)</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '8px' }}>
              {(currentTech.images || []).map((img, idx) => (
                <div key={idx} className="item-sub-card" style={{ padding: '8px', textAlign: 'center' }}>
                  <span className="item-counter" style={{ display: 'block', marginBottom: '6px' }}>Slide #{idx + 1}</span>
                  <div style={{ width: '100%', height: '120px', borderRadius: '6px', overflow: 'hidden', background: '#f1f5f9', marginBottom: '8px' }}>
                    <img src={resolveImage(img)} alt={`Slide ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <label className="btn-upload-unified" style={{ display: 'inline-flex', padding: '6px 12px', fontSize: '12px' }}>
                    <Upload size={12} />
                    <span>Ganti Foto</span>
                    <input type="file" accept="image/*" onChange={e => handleImageReplace(idx, e)} disabled={imageUploading} style={{ display: 'none' }} />
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group full-width">
            <label>Full Content Narrative (Ulasan Lengkap Teknologi & Pemanfaatannya)</label>
            <textarea
              rows={4}
              value={currentTech.content}
              onChange={e => handleTechFieldChange('content', e.target.value)}
              placeholder="Ulasan detail penerapan teknologi bagi pembelajaran siswa..."
            />
          </div>
        </div>

        {/* Features & Highlights */}
        <div className="sub-section-divider">
          <h4>Spesifikasi Fitur Utama & Poin Sorotan</h4>
        </div>

        <div className="card-body form-grid-2">
          {/* Features */}
          <div className="item-sub-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span className="item-counter">Spesifikasi Fitur Utama</span>
              <button type="button" className="btn-field-save" onClick={handleAddFeature}>
                <Plus size={14} /> Tambah Fitur
              </button>
            </div>
            {(currentTech.features || []).map((feat, idx) => (
              <div key={idx} style={{ marginBottom: '12px', padding: '12px', background: '#ffffff', borderRadius: '10px', border: '1.5px solid #e2e8f0' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={feat.title}
                    onChange={e => handleFeatureChange(idx, 'title', e.target.value)}
                    placeholder="Judul Fitur"
                    style={{ flex: 1 }}
                  />
                  <button type="button" className="btn-item-delete" onClick={() => handleRemoveFeature(idx)} title="Hapus Fitur">
                    <Trash2 size={14} />
                  </button>
                </div>
                <input
                  type="text"
                  value={feat.detail}
                  onChange={e => handleFeatureChange(idx, 'detail', e.target.value)}
                  placeholder="Detail penjelasan fitur..."
                />
              </div>
            ))}
          </div>

          {/* Highlights */}
          <div className="item-sub-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <span className="item-counter">Highlights & Kapasitas</span>
              <button type="button" className="btn-field-save" onClick={handleAddHighlight}>
                <Plus size={14} /> Tambah Poin
              </button>
            </div>
            {(currentTech.highlights || []).map((h, idx) => (
              <div key={idx} className="input-with-action" style={{ marginBottom: '10px' }}>
                <input
                  type="text"
                  value={h}
                  onChange={e => handleHighlightChange(idx, e.target.value)}
                  placeholder="Poin sorotan..."
                />
                <button type="button" className="btn-item-delete" onClick={() => handleRemoveHighlight(idx)} title="Hapus Poin">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="sub-section-divider">
          <h4>Keunggulan & Manfaat bagi Siswa (Benefits)</h4>
        </div>

        <div className="card-body">
          <div className="item-sub-card full-width">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <span className="item-counter">Daftar Manfaat & Dampak Positif</span>
              <button type="button" className="btn-field-save" onClick={handleAddBenefit}>
                <Plus size={14} /> Tambah Manfaat
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '10px' }}>
              {(currentTech.benefits || []).map((b, idx) => (
                <div key={idx} className="input-with-action">
                  <input
                    type="text"
                    value={b}
                    onChange={e => handleBenefitChange(idx, e.target.value)}
                    placeholder="Manfaat teknologi..."
                  />
                  <button type="button" className="btn-item-delete" onClick={() => handleRemoveBenefit(idx)} title="Hapus Manfaat">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
